import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Tent, Flame, Mountain } from 'lucide-react';
import { PricingTier } from '@/types/pricing';

const iconMap = {
  tent: Tent,
  campfire: Flame,
  mountain: Mountain,
};

interface PricingCardProps {
  tier: PricingTier;
  onSelect: () => void;
  loading?: boolean;
}

export const PricingCard = ({ tier, onSelect, loading }: PricingCardProps) => {
  const IconComponent = tier.icon ? iconMap[tier.icon] : null;
  
  return (
    <Card className={`relative h-full transition-all duration-300 hover:shadow-xl border-copper/20 bg-ivory/80 backdrop-blur-sm ${
      tier.popular ? 'scale-105 border-2 border-copper shadow-lg shadow-copper/20' : ''
    }`}>
      {tier.popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <Badge className="bg-copper text-white px-4 py-1 shadow-md">Most Popular</Badge>
        </div>
      )}
      
      <CardHeader className="text-center pb-4">
        {IconComponent && (
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-full bg-gradient-to-br from-copper/20 to-copper/5 border-2 border-copper/30">
              <IconComponent className="h-8 w-8 text-copper" />
            </div>
          </div>
        )}
        
        <CardTitle className="text-2xl font-playfair font-bold text-midnight-green">{tier.name}</CardTitle>
        <CardDescription className="text-deep-teal">{tier.description}</CardDescription>
        
        <div className="mt-4">
          <div className="text-3xl font-bold text-midnight-green">
            {tier.displayPrice}
          </div>
          
          {tier.billingSubtext && (
            <div className="text-sm text-green-600 font-medium mt-2">
              {tier.billingSubtext}
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <ul className="space-y-3">
          {tier.features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>
        
        <div className="pt-4">
          <Button
            onClick={onSelect}
            disabled={loading}
            className={`w-full ${
              tier.popular 
                ? 'bg-copper hover:bg-copper/90 text-white shadow-lg' 
                : 'bg-midnight-green hover:bg-midnight-green/90 text-white'
            }`}
            size="lg"
          >
            {loading ? 'Processing...' : tier.ctaText}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
